import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { select, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  NEVER,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { Memoize } from 'typescript-memoize';

import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { injectReportsTranslations } from '@core/reports-contracts';
import { ReportsComponentsSelectors, ReportsSelectors } from '@core/reports-data';
import {
  ComponentFacadeParams,
  ReportDashboardComponentViewData,
  ReportDashboardLibraryComponent,
  ReportDashboardTemplateComponent,
  ReportResultsDataParams,
  VisualizationDataTypeUnion,
  VisualizationOptions,
} from '@core/reports-types';
import { mapToVisualization } from '@core/reports-ui';
import { DurationPipe } from '@core/user-interface-legacy';

import {
  componentFacadeParamsMemoizeFn,
  ReportComponentsDataFacade,
} from './components-data.facade';
import { ReportsConfigurationFacade } from './reports-configuration.facade';
import { ReportsResultsFacade } from './results.facade';

const visualizationParamsMemoizeFn = (
  params: ComponentFacadeParams,
  options?: VisualizationOptions,
) => {
  const paramsMemoizeResult = componentFacadeParamsMemoizeFn(params);
  if (!options || !options.truncateDataset || typeof options.truncateDataset === 'number') {
    return `${paramsMemoizeResult}#${JSON.stringify(options)}`;
  }
  // It's a hack, but we have to return something that will never match
  // because if truncate is a function, we basically have to remove the memoization
  // This is why truncating the dataset has to be done on the component level
  // TODO: Remove when building configs is moved from visualization pipe to functional approach
  return performance.now();
};

@Injectable({
  providedIn: 'root',
})
export class ReportComponentsVisualizationsFacade {
  private readonly componentData = inject(ReportComponentsDataFacade);
  private readonly configFacade = inject(ReportsConfigurationFacade);
  private readonly i18n = inject(I18nService);
  private readonly resultsFacade = inject(ReportsResultsFacade);
  private readonly reportsTranslations = injectReportsTranslations();
  private readonly store = inject(Store);

  public readonly sanitizer = inject(DomSanitizer);

  private durationPipe = new DurationPipe(this.i18n);

  /**
   * Loads visualization data once for given parameters
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  public loadComponentVisualizationDataOnce(
    params: ComponentFacadeParams,
    options?: VisualizationOptions,
  ): void {
    this.getComponentVisualizationDataParams(params)
      .pipe(
        take(1),
        switchMap(data => this.dispatchLoadVisualization(data, params, options)),
      )
      .subscribe();
  }
  /**
   * When subscribed, will load visualization data for given parameters at subscription, and then at every change of component data params
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  public loadComponentVisualizationDataContinuously$(
    params: ComponentFacadeParams,
    options?: VisualizationOptions,
  ) {
    return this.getComponentVisualizationDataParams(params).pipe(
      distinctUntilChanged((a, b) => this.resultsFacade.areParamsEqual(a, b)),
      switchMap(data => this.dispatchLoadVisualization(data, params, options)),
    );
  }

  @Memoize(visualizationParamsMemoizeFn)
  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  public getComponentVisualization(
    params: ComponentFacadeParams,
    options: VisualizationOptions = {},
  ): Observable<VisualizationDataTypeUnion> {
    const data$ = this.componentData.getComponentRawData(params);
    const metric$ = this.componentData.getComponentMetric(params);
    const component$ =
      'component' in params
        ? of(params.component)
        : this.componentData.getComponent(params.componentId, params.dashboardId);

    return combineLatest([component$, data$, metric$]).pipe(
      map(([component, data, metric]) => ({
        component,
        data,
        metric,
        meta: { ...options },
      })),
      mapToVisualization(this.sanitizer, this.reportsTranslations, this.durationPipe),
      catchError(err => {
        console.error(err);
        return NEVER;
      }),
      shareReplay(1),
    );
  }

  @Memoize(visualizationParamsMemoizeFn)
  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  public getComponentVisualizationSync(
    params: ComponentFacadeParams,
    options: VisualizationOptions = {},
  ): Observable<{
    data: VisualizationDataTypeUnion;
    component: ReportDashboardComponentViewData;
  }> {
    const component$ =
      'component' in params
        ? of(params.component)
        : this.componentData.getComponent(params.componentId, params.dashboardId);
    return combineLatest([this.getComponentVisualization(params, options), component$]).pipe(
      map(([data, component]) => ({ data, component })),
      shareReplay(1),
    );
  }

  @Memoize(c => c.libraryId)
  /**
   *
   * @param params - either a full component or componentId and dashboardId ONLY FOR components in store/components (not library-components)
   */
  public getComponentSampleDataVisualization(
    comp: ReportDashboardLibraryComponent | ReportDashboardTemplateComponent,
  ): Observable<VisualizationDataTypeUnion> {
    const component: ReportDashboardComponentViewData = comp;
    return this.configFacade.getMetricBySlug(component.metric).pipe(
      take(1),
      map(metric => ({
        component,
        data: { results: comp.sampleData },
        metric,
        meta: {},
      })),
      mapToVisualization(this.sanitizer, this.reportsTranslations, this.durationPipe),
    );
  }

  @Memoize(componentFacadeParamsMemoizeFn)
  private getComponentVisualizationDataParams(
    params: ComponentFacadeParams,
  ): Observable<ReportResultsDataParams> {
    if ('component' in params) {
      return this.store
        .select(ReportsSelectors.createDataParamsForComponent(params.component))
        .pipe(filterBoolean, shareReplay(1));
    }
    const { dashboardId, componentId } = params;
    return this.store.pipe(
      select(
        dashboardId
          ? ReportsSelectors.componentDataParamsWithDashboardFiltersApplied(
              componentId,
              dashboardId,
            )
          : ReportsComponentsSelectors.componentDataParams(componentId),
      ),
      filterBoolean,
      shareReplay(1),
    );
  }

  private dispatchLoadVisualization(
    data: ReportResultsDataParams,
    params: ComponentFacadeParams,
    options?: VisualizationOptions,
  ) {
    return this.resultsFacade.fetchAndGetResultsContinously$(data).pipe(
      take(1),
      switchMap(() => this.getComponentVisualizationSync(params, options)),
    );
  }
}
